'use client';

import { useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import type { Render } from '@/types/render';
import { NoticeBanner, getNoticeBannerActionClasses } from '@/shared/ui/NoticeBanner';
import { Button } from '@/shared/ui/shadcn/button';
import { AnalyticsEvent, trackEventOnceForId } from '@/shared/lib/analytics';
import { openStudioBridge } from '@/features/design/render/lib/studio-bridge';
import { cn } from '@/shared/lib/cn';

/**
 * Info notice under a render offering the Design Capital studio. The API
 * decides which single render carries it (`studio_bridge.notice`); this only
 * shows the server copy and opens the same url as the menu entry.
 */
export function StudioBridgeNotice({
  render,
  className,
}: {
  render: Render | null | undefined;
  className?: string;
}) {
  const bridge = render?.attributes?.studio_bridge;
  const notice = bridge?.notice;
  const renderId = render?.id;

  useEffect(() => {
    if (notice && renderId) {
      trackEventOnceForId(AnalyticsEvent.STUDIO_BRIDGE_SHOWN, `notice:${renderId}`, {
        render_id: renderId,
        placement: 'notice',
      });
    }
  }, [notice, renderId]);

  if (!bridge || !notice || !renderId) return null;

  return (
    <NoticeBanner
      variant="info"
      title={notice.title}
      message={notice.message}
      className={className}
      action={
        <Button
          variant="ghost"
          size="sm"
          className={cn('shrink-0', getNoticeBannerActionClasses('info'))}
          onClick={() => openStudioBridge(bridge, renderId, 'notice')}
        >
          {notice.action}
          <ExternalLink className="h-4 w-4 ms-1" />
        </Button>
      }
    />
  );
}
